import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import i18n from '@ohif/i18n';
import { I18nextProvider } from 'react-i18next';
import { BrowserRouter } from 'react-router-dom';
import Compose from './routes/Mode/Compose';
import CustomAuthenticator from './components/CustomAuthenticator';
import { Amplify, type ResourcesConfig } from 'aws-amplify';
import { Hub } from 'aws-amplify/utils';
import 'aws-amplify/auth/enable-oauth-listener';
import awsConfig from '../aws-exports';
import {
    ServicesManager,
    ExtensionManager,
    CommandsManager,
    HotkeysManager,
    ServiceProvidersManager,
} from '@ohif/core';
import {
    DialogProvider,
    Modal,
    ModalProvider,
    SnackbarProvider,
    ThemeWrapper,
    ViewportDialogProvider,
    ViewportGridProvider,
    CineProvider,
    UserAuthenticationProvider,
    ToolboxProvider,
} from '@ohif/ui';
import { AppConfigProvider } from '@state';
import createRoutes from './routes';
import appInit from './appInit.js';
import '@aws-amplify/ui-react/styles.css';
import './authenticator-styles.css';

import OpenIdConnectRoutes from './utils/OpenIdConnectRoutes';
import StudiesConfigProvider from './providers/studies/studiesConfig';
import FileUploadProvider from './providers/files-upload/filesUpload';

import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import UserProvider from '../../app/src/providers/user/UserContext';

let commandsManager: CommandsManager,
    extensionManager: ExtensionManager,
    servicesManager: ServicesManager,
    serviceProvidersManager: ServiceProvidersManager,
    hotkeysManager: HotkeysManager;

const amplifyConfig: ResourcesConfig = {
    Auth: {
        Cognito: {
            userPoolId: awsConfig.userPoolId as string,
            userPoolClientId: awsConfig.userPoolClientId as string,
            ...(awsConfig.identityPoolId && { identityPoolId: awsConfig.identityPoolId as string }),
            loginWith: {
                username: true,
                oauth: {
                    domain: awsConfig.cognitoDomain as string,
                    scopes: ['email', 'openid', 'profile'],
                    redirectSignIn: awsConfig.redirectSignIn,
                    redirectSignOut: awsConfig.redirectSignOut,
                    responseType: 'code',
                    providers: [
                        'Google',
                        { custom: awsConfig.microsoftCognitoProviderName as string },
                    ],
                },
            },
        },
    },
};

Amplify.configure(amplifyConfig);

Hub.listen('auth', ({ payload }) => {
    switch (payload.event) {
        case 'signInWithRedirect':
            console.info('[Amplify Auth] SSO sign-in successful (Google or Microsoft)');
            break;
        case 'signInWithRedirect_failure':
            console.error('[Amplify Auth] SSO sign-in failed:', payload.data);
            break;
        case 'customOAuthState':
            console.info('[Amplify Auth] Custom OAuth state:', payload.data);
            break;
    }
});

function App({ config, defaultExtensions, defaultModes }) {
    const [init, setInit] = useState(null);
    useEffect(() => {
        const run = async () => {
            appInit(config, defaultExtensions, defaultModes).then(setInit).catch(console.error);
        };

        run();
    }, []);

    if (!init) return null;

    commandsManager = init.commandsManager;
    extensionManager = init.extensionManager;
    servicesManager = init.servicesManager;
    serviceProvidersManager = init.serviceProvidersManager;
    hotkeysManager = init.hotkeysManager;

    const appConfigState = init.appConfig;
    const { routerBasename, modes, dataSources, oidc, showStudyList } = appConfigState;
    const canvas = document.createElement('canvas');
    const gl = canvas.getContext('webgl2');
    const max3DTextureSize = gl.getParameter(gl.MAX_3D_TEXTURE_SIZE);
    appConfigState.max3DTextureSize = max3DTextureSize;

    const {
        uiDialogService,
        uiModalService,
        uiNotificationService,
        uiViewportDialogService,
        viewportGridService,
        cineService,
        userAuthenticationService,
        customizationService,
    } = servicesManager.services;

    const providers = [
        [FileUploadProvider],
        [StudiesConfigProvider],
        [AppConfigProvider, { value: appConfigState }],
        [UserAuthenticationProvider, { service: userAuthenticationService }],
        [I18nextProvider, { i18n }],
        [ThemeWrapper],
        [ToolboxProvider],
        [ViewportGridProvider, { service: viewportGridService }],
        [ViewportDialogProvider, { service: uiViewportDialogService }],
        [CineProvider, { service: cineService }],
        [SnackbarProvider, { service: uiNotificationService }],
        [DialogProvider, { service: uiDialogService }],
        [ModalProvider, { service: uiModalService, modal: Modal }],
    ];
    const providersFromManager = Object.entries(serviceProvidersManager.providers);
    if (providersFromManager.length > 0) {
        providersFromManager.forEach(([serviceName, provider]) => {
            providers.push([provider as any, { service: servicesManager.services[serviceName] }]);
        });
    }

    const CombinedProviders = ({ children }) =>
        Compose({ components: providers, children });

    customizationService.init(extensionManager);
    const appRoutes = createRoutes({
        modes,
        dataSources,
        extensionManager,
        servicesManager,
        commandsManager,
        hotkeysManager,
        routerBasename,
        showStudyList,
    });

    let authRoutes = null;

    if (oidc) {
        authRoutes = (
            <OpenIdConnectRoutes
                oidc={oidc}
                routerBasename={routerBasename}
                userAuthenticationService={userAuthenticationService}
            />
        );
    }
    return (
        <UserProvider>
            <CombinedProviders>
                <ToastContainer />
                <BrowserRouter basename={routerBasename}>
                    {authRoutes}
                    <CustomAuthenticator appRoutes={appRoutes} />
                </BrowserRouter>
            </CombinedProviders>
        </UserProvider>
    );
}

App.propTypes = {
    config: PropTypes.oneOfType([
        PropTypes.func,
        PropTypes.shape({
            routerBasename: PropTypes.string.isRequired,
            oidc: PropTypes.array,
            whiteLabeling: PropTypes.object,
            extensions: PropTypes.array,
        }),
    ]).isRequired,
    defaultExtensions: PropTypes.array,
    defaultModes: PropTypes.array,
};

App.defaultProps = {
    config: {
        routerBaseName: '/',
        showLoadingIndicator: true,
        showStudyList: true,
        oidc: [],
        extensions: [],
    },
    defaultExtensions: [],
};

export default App;
export { commandsManager, extensionManager, servicesManager };
